import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

function AdminPanel() {
  const [activeTab, setActiveTab] = useState('stats');
  const [satdList, setSatdList] = useState([]);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [allAccounts, setAllAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [migrationRunning, setMigrationRunning] = useState(false);
  const [migrationResult, setMigrationResult] = useState(null);

  const token = localStorage.getItem('token');

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const statsRes = await axios.get(`${API_URL}/stats`, config);
      setStats(statsRes.data);

      if (activeTab === 'satd') {
        const satdRes = await axios.get(`${API_URL}/satd`, config).catch(() => ({ data: [] }));
        setSatdList(satdRes.data);
      }
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleMigration = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir migrer les données ?\n\nCela va créer une sauvegarde automatique et ajouter les nouvelles fonctionnalités.')) {
      return;
    }

    setMigrationRunning(true);
    setMigrationResult(null);

    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const { data } = await axios.post(`${API_URL}/admin/trigger-migration`, {}, config);
      
      setMigrationResult({
        success: data.success,
        message: data.success ? '✅ Migration réussie !' : '❌ Migration échouée',
        output: data.output,
        error: data.error
      });

      if (data.success) {
        loadData(); // Recharger les stats
      }
    } catch (err) {
      setMigrationResult({
        success: false,
        message: '❌ Erreur lors de la migration',
        error: err.response?.data?.detail || err.message
      });
    } finally {
      setMigrationRunning(false);
    }
  };

  if (loading) {
    return <div className="text-center py-20">Chargement...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">⚙️ Gestion Admin</h1>
        <button
          onClick={handleMigration}
          disabled={migrationRunning}
          className={`px-6 py-3 rounded-lg font-medium transition-all ${
            migrationRunning 
              ? 'bg-gray-400 cursor-not-allowed' 
              : 'bg-gradient-to-r from-purple-600 to-purple-700 text-white hover:from-purple-700 hover:to-purple-800 shadow-lg'
          }`}
        >
          {migrationRunning ? '⏳ Migration en cours...' : '🔄 Migrer les Données'}
        </button>
      </div>

      {/* Migration Result */}
      {migrationResult && (
        <div className={`p-4 rounded-lg border-l-4 ${
          migrationResult.success 
            ? 'bg-green-50 border-green-500' 
            : 'bg-red-50 border-red-500'
        }`}>
          <p className={`font-semibold mb-2 ${
            migrationResult.success ? 'text-green-800' : 'text-red-800'
          }`}>
            {migrationResult.message}
          </p>
          {migrationResult.output && (
            <pre className="text-xs bg-white p-3 rounded mt-2 overflow-auto max-h-40">
              {migrationResult.output}
            </pre>
          )}
          {migrationResult.error && (
            <p className="text-sm text-red-700 mt-2">Erreur: {migrationResult.error}</p>
          )}
        </div>
      )}

      {/* Admin Stats */}
      {stats?.admin_stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-sm p-6 text-white">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium">Utilisateurs totaux</h3>
              <span className="text-2xl">👥</span>
            </div>
            <p className="text-4xl font-bold">{stats.admin_stats.total_users}</p>
          </div>

          <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl shadow-sm p-6 text-white">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium">Comptes totaux</h3>
              <span className="text-2xl">🏦</span>
            </div>
            <p className="text-4xl font-bold">{stats.admin_stats.total_accounts}</p>
          </div>

          <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl shadow-sm p-6 text-white">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium">Total transactions</h3>
              <span className="text-2xl">💸</span>
            </div>
            <p className="text-4xl font-bold">{stats.admin_stats.total_transactions}</p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            <button
              onClick={() => setActiveTab('stats')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'stats'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              📊 Statistiques
            </button>
            <button
              onClick={() => setActiveTab('accounts')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'accounts'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              🏦 Gestion Comptes
            </button>
            <button
              onClick={() => setActiveTab('recurring')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'recurring'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              🔄 Paiements Récurrents
            </button>
            <button
              onClick={() => setActiveTab('satd')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'satd'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              ⚖️ SATD
            </button>
            <button
              onClick={() => setActiveTab('staff')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'staff'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              👥 Gestion Staff
            </button>
            <button
              onClick={() => setActiveTab('central-bank')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'central-bank'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              🏛️ Banque Centrale
            </button>
            <button
              onClick={() => setActiveTab('elb-management')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'elb-management'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              💎 e-£B
            </button>
            <button
              onClick={() => setActiveTab('currency-monitor')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'currency-monitor'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              📊 Devises
            </button>
            <button
              onClick={() => setActiveTab('kyc')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'kyc'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              🆔 KYC
            </button>
            <button
              onClick={() => setActiveTab('loans')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'loans'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              💰 Crédits
            </button>
            <button
              onClick={() => setActiveTab('bad-clients')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'bad-clients'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              ⚠️ Mauvais Clients
            </button>
            <button
              onClick={() => setActiveTab('closed-accounts')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'closed-accounts'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              🔒 Comptes Clôturés
            </button>
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'stats' && <StatsTab stats={stats} />}
          {activeTab === 'accounts' && <AccountsManagementTab token={token} />}
          {activeTab === 'recurring' && <RecurringPaymentsTab token={token} />}
          {activeTab === 'satd' && <SATDTab token={token} satdList={satdList} loadData={loadData} />}
          {activeTab === 'staff' && <StaffManagementTab token={token} />}
          {activeTab === 'central-bank' && <CentralBankTab token={token} />}
          {activeTab === 'elb-management' && <ELBManagementTab token={token} />}
          {activeTab === 'currency-monitor' && <CurrencyMonitorTab token={token} />}
          {activeTab === 'kyc' && <KYCTab token={token} />}
          {activeTab === 'loans' && <LoansManagementTab token={token} />}
          {activeTab === 'bad-clients' && <BadClientsTab token={token} />}
          {activeTab === 'closed-accounts' && <ClosedAccountsTab token={token} />}
        </div>
      </div>
    </div>
  );
}

// Stats Tab
function StatsTab({ stats }) {
  return (
    <div className="space-y-4">
      <div className="bg-gradient-to-r from-red-500 to-orange-500 rounded-xl p-6 text-white">
        <h2 className="text-2xl font-bold mb-2">⚖️ SATD - Saisie Administrative à Tiers Détenteur</h2>
        <p className="text-red-100 mb-4">
          Récupération des dettes fiscales de 3240 milliards d'euros du Trésor Public Français
        </p>
        <div className="bg-white/20 rounded-lg p-4 backdrop-blur-sm">
          <p className="font-semibold mb-1">Dette totale à récupérer</p>
          <p className="text-3xl font-bold">3 240 000 000 000,00 €</p>
          <p className="text-sm text-red-100 mt-2">Conséquence de 37 ans de combat pour l'indépendance bretonne</p>
        </div>
      </div>
      
      <div className="bg-blue-50 rounded-lg p-4">
        <p className="text-sm text-blue-900">
          Utilisez les onglets ci-dessus pour gérer les comptes, les paiements récurrents et les SATD.
        </p>
      </div>
    </div>
  );
}

// Accounts Management Tab
function AccountsManagementTab({ token }) {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [users, setUsers] = useState({});
  const [allTransactions, setAllTransactions] = useState([]);
  const [allLoans, setAllLoans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBalanceModal, setShowBalanceModal] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [newBalance, setNewBalance] = useState('');
  const [expandedAccount, setExpandedAccount] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc' ou 'desc'
  const [formData, setFormData] = useState({
    create_new_user: true,
    user_name: '',
    user_email: '',
    user_password: '',
    user_address: '',
    existing_user_email: '',
    account_type: 'checking',
    initial_balance: '100'
  });

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    console.log('Loading all data... Token:', token ? 'exists' : 'missing');
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const [accountsRes, usersRes, transactionsRes, loansRes] = await Promise.all([
        axios.get(`${API_URL}/admin/accounts`, config),
        axios.get(`${API_URL}/admin/users`, config),
        axios.get(`${API_URL}/admin/transactions`, config),
        axios.get(`${API_URL}/admin/loans`, config)
      ]);
      
      console.log('Accounts loaded:', accountsRes.data);
      console.log('Users loaded:', usersRes.data);
      console.log('Transactions loaded:', transactionsRes.data);
      console.log('Loans loaded:', loansRes.data);
      
      setAccounts(accountsRes.data);
      setAllTransactions(transactionsRes.data);
      setAllLoans(loansRes.data);
      
      const usersMap = {};
      usersRes.data.forEach(user => {
        usersMap[user.id] = user;
      });
      setUsers(usersMap);
      console.log('Users map created:', Object.keys(usersMap).length);
    } catch (err) {
      console.error('Error loading data:', err);
      console.error('Error details:', err.response?.data || err.message);
    } finally {
      setLoading(false);
    }
  };


  // Filtrer et trier les comptes
  const getFilteredAndSortedAccounts = () => {
    if (!accounts || accounts.length === 0) {
      console.log('No accounts to filter');
      return [];
    }
    
    console.log('Accounts:', accounts.length, 'Users:', Object.keys(users).length);
    let filtered = [...accounts];

    // Filtrer par recherche
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(account => {
        const user = users[account.user_id];
        return (
          account.iban?.toLowerCase().includes(query) ||
          account.reference_number?.toLowerCase().includes(query) ||
          account.account_number?.toLowerCase().includes(query) ||
          user?.name?.toLowerCase().includes(query) ||
          user?.email?.toLowerCase().includes(query)
        );
      });
      console.log('Filtered by search:', filtered.length);
    }

    // Trier par ordre alphabétique du nom du client (seulement si users est chargé)
    if (Object.keys(users).length > 0) {
      filtered.sort((a, b) => {
        const userA = users[a.user_id];
        const userB = users[b.user_id];
        const nameA = userA?.name || 'ZZZ';  // Mettre les sans nom à la fin
        const nameB = userB?.name || 'ZZZ';
        
        if (sortOrder === 'asc') {
          return nameA.localeCompare(nameB);
        } else {
          return nameB.localeCompare(nameA);
        }
      });
    }

    console.log('Final filtered accounts:', filtered.length);
    return filtered;
  };


  const handleCreateAccount = async (e) => {
    e.preventDefault();
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      if (formData.create_new_user) {
        // Créer un nouvel utilisateur avec son compte
        const registerResponse = await axios.post(`${API_URL}/auth/register`, {
          name: formData.user_name,
          email: formData.user_email,
          password: formData.user_password,
          role_type: 'client'
        });
        
        // Si le solde initial n'est pas 100€, on doit le modifier
        if (parseFloat(formData.initial_balance) !== 100) {
          // Récupérer l'ID du compte créé automatiquement
          const userAccounts = await axios.get(`${API_URL}/accounts`, {
            headers: { Authorization: `Bearer ${registerResponse.data.token}` }
          });
          
          if (userAccounts.data.length > 0) {
            const accountId = userAccounts.data[0].id;
            await axios.patch(
              `${API_URL}/admin/accounts/${accountId}/balance`,
              null,
              { ...config, params: { new_balance: parseFloat(formData.initial_balance) } }
            );
          }
        }
        
        alert(`Client "${formData.user_name}" créé avec succès !\nEmail: ${formData.user_email}\nMot de passe: ${formData.user_password}`);
      } else {
        // Créer un compte pour un utilisateur existant
        await axios.post(`${API_URL}/admin/accounts`, null, {
          ...config,
          params: {
            user_email: formData.existing_user_email,
            account_type: formData.account_type,
            initial_balance: parseFloat(formData.initial_balance)
          }
        });
        alert('Compte créé avec succès pour le client existant !');
      }
      
      setShowCreateForm(false);
      setFormData({
        create_new_user: true,
        user_name: '',
        user_email: '',
        user_password: '',
        user_address: '',
        existing_user_email: '',
        account_type: 'checking',
        initial_balance: '100'
      });
      loadAllData();
    } catch (err) {
      alert('Erreur: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleModifyBalance = async () => {
    if (!selectedAccount || !newBalance) return;
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.patch(
        `${API_URL}/admin/accounts/${selectedAccount.id}/balance`,
        null,
        { ...config, params: { new_balance: parseFloat(newBalance) } }
      );
      alert('Solde modifié avec succès !');
      setShowBalanceModal(false);
      setSelectedAccount(null);
      setNewBalance('');
      loadAllData();
    } catch (err) {
      alert('Erreur: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleSuspendAccount = async (account) => {
    const newStatus = account.status === 'active' ? 'suspended' : 'active';
    const confirmation = window.confirm(
      `Voulez-vous ${newStatus === 'suspended' ? 'suspendre' : 'activer'} ce compte ?`
    );
    
    if (!confirmation) return;
    
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.patch(
        `${API_URL}/admin/accounts/${account.id}/status`,
        null,
        { ...config, params: { status: newStatus } }
      );
      alert(`Compte ${newStatus === 'suspended' ? 'suspendu' : 'activé'} avec succès !`);
      loadAllData();
    } catch (err) {
      alert('Erreur: ' + (err.response?.data?.detail || err.message));
    }
  };

  const getAccountTransactions = (accountId) => {
    return allTransactions.filter(
      tx => tx.from_account === accountId || tx.to_account === accountId
    );
  };

  if (loading) {
    return <div className="text-center py-10">Chargement des comptes...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold text-gray-900">Gestion des Comptes</h2>
        <button
          onClick={() => setShowCreateForm(!showCreateForm)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          {showCreateForm ? 'Annuler' : '+ Créer un compte'}
        </button>
      </div>

      {showCreateForm && (
        <div className="bg-white rounded-lg border-2 border-blue-200 p-6">
          <h3 className="text-lg font-bold mb-4">Créer un nouveau compte bancaire</h3>
          
          <div className="flex gap-4 mb-6">
            <button
              type="button"
              onClick={() => setFormData({ ...formData, create_new_user: true })}
              className={`flex-1 py-2 rounded-lg font-semibold transition-colors ${
                formData.create_new_user
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Nouveau client
            </button>
            <button
              type="button"
              onClick={() => setFormData({ ...formData, create_new_user: false })}
              className={`flex-1 py-2 rounded-lg font-semibold transition-colors ${
                !formData.create_new_user
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Client existant
            </button>
          </div>

          <form onSubmit={handleCreateAccount} className="space-y-4">
            {formData.create_new_user ? (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nom du titulaire *
                  </label>
                  <input
                    type="text"
                    value={formData.user_name}
                    onChange={(e) => setFormData({ ...formData, user_name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    placeholder="Jean Dupont"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email *
                  </label>
                  <input
                    type="email"
                    value={formData.user_email}
                    onChange={(e) => setFormData({ ...formData, user_email: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    placeholder="jean.dupont@exemple.com"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Adresse complète *
                  </label>
                  <textarea
                    value={formData.user_address}
                    onChange={(e) => setFormData({ ...formData, user_address: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    placeholder="15 Rue de Rennes, 35000 Rennes, Bretagne"
                    rows="2"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Mot de passe initial *
                  </label>
                  <input
                    type="text"
                    value={formData.user_password}
                    onChange={(e) => setFormData({ ...formData, user_password: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    placeholder="Minimum 6 caractères"
                    required
                    minLength="6"
                  />
                </div>
              </>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email du client existant *
                </label>
                <input
                  type="email"
                  value={formData.existing_user_email}
                  onChange={(e) => setFormData({ ...formData, existing_user_email: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  placeholder="client@exemple.com"
                  required
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type de compte *
              </label>
              <select
                value={formData.account_type}
                onChange={(e) => setFormData({ ...formData, account_type: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              >
                <option value="checking">Compte Courant</option>
                <option value="savings">Compte Épargne</option>
                <option value="investment">Compte Investissement</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Solde initial (€) *
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.initial_balance}
                onChange={(e) => setFormData({ ...formData, initial_balance: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                required
              />
            </div>

            <button
              type="submit"
              className="w-full px-6 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700"
            >
              Créer le compte
            </button>
          </form>
        </div>
      )}

      {/* Barre de recherche et tri */}
      <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-200">
        <div className="flex gap-4 items-center">
          <div className="flex-1 relative">
            <input
              type="text"
              placeholder="🔍 Rechercher par nom, email, IBAN, référence..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 pl-10"
            />
            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-xl">
              🔍
            </span>
          </div>
          <button
            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            className="px-4 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 flex items-center gap-2"
          >
            {sortOrder === 'asc' ? '↑ A-Z' : '↓ Z-A'}
          </button>
        </div>
        {!loading && accounts && (
          <p className="text-xs text-gray-500 mt-2">
            {getFilteredAndSortedAccounts().length} compte(s) trouvé(s) sur {accounts.length} total
          </p>
        )}
      </div>

      {/* Liste des comptes */}
      <div className="space-y-4">
        <h3 className="text-lg font-bold text-gray-900">
          {searchQuery ? 'Résultats de la recherche' : 'Liste de tous les comptes'}
        </h3>
        
        {getFilteredAndSortedAccounts().length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm p-12 border border-gray-200 text-center">
            <p className="text-gray-500">
              {searchQuery ? 'Aucun compte trouvé pour cette recherche' : 'Aucun compte disponible'}
            </p>
          </div>
        ) : (
          <div className="grid gap-4">
            {getFilteredAndSortedAccounts().map((account) => (
              <div
                key={account.id}
                className="bg-white rounded-xl shadow-sm p-6 border border-gray-200"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <span className="text-3xl">
                      {account.account_type === 'checking' && '💳'}
                      {account.account_type === 'savings' && '💰'}
                      {account.account_type === 'investment' && '📈'}
                    </span>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">
                        {account.account_type === 'checking' && 'Compte Courant'}
                        {account.account_type === 'savings' && 'Compte Épargne'}
                        {account.account_type === 'investment' && 'Compte Investissement'}
                      </h3>
                      <p className="text-sm font-semibold text-blue-600">
                        👤 {users[account.user_id]?.name || 'Chargement...'}
                      </p>
                      <p className="text-xs text-gray-500">
                        {users[account.user_id]?.email || ''} • ID: {account.id.substring(0, 8)}... • 
                        <span className={`ml-2 px-2 py-1 rounded-full text-xs ${
                          account.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                        }`}>
                          {account.status}
                        </span>
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-3xl font-bold text-gray-900">
                      {account.balance?.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
                    </p>
                    <p className="text-sm text-gray-500">{account.currency}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 pt-4 border-t border-gray-200">
                  <div>
                    <p className="text-xs text-gray-600">IBAN</p>
                    <p className="font-mono text-sm font-semibold">{account.iban || account.account_number}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600">BIC/SWIFT</p>
                    <p className="font-mono text-sm font-semibold">{account.bic}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600">Créé le</p>
                    <p className="text-sm font-semibold">
                      {new Date(account.created_at).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                </div>

                {/* Actions Admin */}
                <div className="flex gap-2 mt-4 pt-4 border-t border-gray-200">
                  <button 
                    onClick={() => {
                      setSelectedAccount(account);
                      setNewBalance(account.balance.toString());
                      setShowBalanceModal(true);
                    }}
                    className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700"
                  >
                    💰 Modifier solde
                  </button>
                  <button 
                    onClick={() => {
                      setExpandedAccount(expandedAccount === account.id ? null : account.id);
                    }}
                    className="px-4 py-2 bg-gray-600 text-white text-sm rounded-lg hover:bg-gray-700"
                  >
                    📊 {expandedAccount === account.id ? 'Masquer' : 'Voir'} transactions ({getAccountTransactions(account.id).length})
                  </button>
                  <button 
                    onClick={() => handleSuspendAccount(account)}
                    className={`px-4 py-2 text-white text-sm rounded-lg ${
                      account.status === 'active' 
                        ? 'bg-red-600 hover:bg-red-700' 
                        : 'bg-green-600 hover:bg-green-700'
                    }`}
                  >
                    {account.status === 'active' ? '🚫 Suspendre' : '✅ Activer'}
                  </button>
                </div>

                {/* Transactions & Loans expandable */}
                {expandedAccount === account.id && (
                  <div className="mt-4 pt-4 border-t border-gray-200 space-y-4">
                    {/* Crédits en cours */}
                    <div>
                      <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                        <span>💰</span> Crédits en cours
                      </h4>
                      <div className="space-y-2">
                        {allLoans.filter(loan => loan.account_id === account.id && ['approved', 'disbursed'].includes(loan.status)).length === 0 ? (
                          <p className="text-gray-500 text-sm text-center py-2 bg-gray-50 rounded-lg">Aucun crédit en cours</p>
                        ) : (
                          allLoans.filter(loan => loan.account_id === account.id && ['approved', 'disbursed'].includes(loan.status)).map((loan) => (
                            <div key={loan.id} className="bg-gradient-to-r from-orange-50 to-yellow-50 rounded-lg p-3 border-l-4 border-orange-400">
                              <div className="flex justify-between items-start">
                                <div className="flex-1">
                                  <p className="font-semibold text-gray-900">{loan.reference_number}</p>
                                  <p className="text-xs text-gray-600 mt-1">
                                    Type: <span className="font-medium">{loan.loan_type}</span>
                                  </p>
                                  <p className="text-xs text-gray-600">
                                    Durée: <span className="font-medium">{loan.duration_months} mois</span> | 
                                    Taux: <span className="font-medium text-blue-600">{loan.interest_rate}%</span>
                                  </p>
                                </div>
                                <div className="text-right">
                                  <p className="text-lg font-bold text-orange-600">
                                    {loan.amount_approved?.toLocaleString('fr-FR')} €
                                  </p>
                                  <p className="text-xs text-gray-600 mt-1">
                                    Mensualité: <span className="font-semibold">{loan.monthly_payment?.toFixed(2)} €</span>
                                  </p>
                                  <span className={`text-xs px-2 py-1 rounded-full mt-1 inline-block ${
                                    loan.status === 'disbursed' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                                  }`}>
                                    {loan.status === 'disbursed' ? 'Versé' : 'Approuvé'}
                                  </span>
                                </div>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>

                    {/* Transactions */}
                    <div>
                      <h4 className="font-bold text-gray-900 mb-3">💸 Transactions</h4>
                      <div className="space-y-2 max-h-64 overflow-y-auto">
                        {getAccountTransactions(account.id).length === 0 ? (
                          <p className="text-gray-500 text-sm text-center py-4">Aucune transaction</p>
                        ) : (
                          getAccountTransactions(account.id).map((tx) => (
                            <div key={tx.id} className="bg-gray-50 rounded-lg p-3 text-sm">
                              <div className="flex justify-between items-start">
                                <div className="flex-1">
                                  <p className="font-semibold text-gray-900">{tx.description}</p>
                                  <p className="text-xs text-gray-500 mt-1">
                                    {new Date(tx.created_at).toLocaleString('fr-FR')}
                                  </p>
                                  <p className="text-xs text-gray-600 mt-1">
                                    {tx.from_account === account.id ? '→ Vers: ' : '← De: '}
                                    <span className="font-mono">{tx.from_account === account.id ? tx.to_account?.substring(0, 20) : tx.from_account?.substring(0, 20)}...</span>
                                  </p>
                                </div>
                                <div className="text-right">
                                  <p className={`text-lg font-bold ${
                                    tx.from_account === account.id ? 'text-red-600' : 'text-green-600'
                                  }`}>
                                    {tx.from_account === account.id ? '-' : '+'}{tx.amount?.toFixed(2)} €
                                  </p>
                                  <span className={`text-xs px-2 py-1 rounded-full ${
                                    tx.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                                  }`}>
                                    {tx.status}
                                  </span>
                                </div>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal Modifier Solde */}
      {showBalanceModal && selectedAccount && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md">
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              Modifier le solde
            </h3>
            <div className="mb-4">
              <p className="text-sm text-gray-600">Compte de : <strong>{users[selectedAccount.user_id]?.name}</strong></p>
              <p className="text-sm text-gray-600">IBAN : <strong>{selectedAccount.iban}</strong></p>
              <p className="text-sm text-gray-600">Solde actuel : <strong className="text-blue-600">{selectedAccount.balance.toFixed(2)} €</strong></p>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nouveau solde (€) *
              </label>
              <input
                type="number"
                step="0.01"
                value={newBalance}
                onChange={(e) => setNewBalance(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                autoFocus
              />
            </div>

            <div className="flex gap-2">
              <button
                onClick={handleModifyBalance}
                className="flex-1 px-4 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700"
              >
                Valider
              </button>
              <button
                onClick={() => {
                  setShowBalanceModal(false);
                  setSelectedAccount(null);
                  setNewBalance('');
                }}
                className="flex-1 px-4 py-3 bg-gray-500 text-white rounded-lg font-semibold hover:bg-gray-600"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Recurring Payments Tab
function RecurringPaymentsTab({ token }) {
  const [showForm, setShowForm] = useState(false);
  const [type, setType] = useState('virement'); // virement ou prelevement
  const [formData, setFormData] = useState({
    from_account: '',
    to_account: '',
    amount: '',
    frequency: 'monthly',
    description: '',
    start_date: new Date().toISOString().split('T')[0]
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    alert(`${type === 'virement' ? 'Virement' : 'Prélèvement'} récurrent créé avec succès !`);
    setShowForm(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold text-gray-900">Paiements Récurrents</h2>
        <button
          onClick={() => setShowForm(!showForm)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          {showForm ? 'Annuler' : '+ Nouveau paiement récurrent'}
        </button>
      </div>

      {showForm && (
        <div className="bg-white rounded-lg border-2 border-blue-200 p-6">
          <div className="flex gap-4 mb-6">
            <button
              onClick={() => setType('virement')}
              className={`flex-1 py-3 rounded-lg font-semibold transition-colors ${
                type === 'virement'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              💸 Virement Récurrent
            </button>
            <button
              onClick={() => setType('prelevement')}
              className={`flex-1 py-3 rounded-lg font-semibold transition-colors ${
                type === 'prelevement'
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              📥 Prélèvement Récurrent
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Compte source (IBAN) *
              </label>
              <input
                type="text"
                value={formData.from_account}
                onChange={(e) => setFormData({ ...formData, from_account: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                placeholder="SC74 2004 1000 1234 5678 9012 3456 789"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Compte destinataire (IBAN) *
              </label>
              <input
                type="text"
                value={formData.to_account}
                onChange={(e) => setFormData({ ...formData, to_account: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                placeholder="SC74 2004 1000 1234 5678 9012 3456 789"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Montant (€) *
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Fréquence *
                </label>
                <select
                  value={formData.frequency}
                  onChange={(e) => setFormData({ ...formData, frequency: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="weekly">Hebdomadaire</option>
                  <option value="monthly">Mensuel</option>
                  <option value="quarterly">Trimestriel</option>
                  <option value="yearly">Annuel</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date de début *
              </label>
              <input
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description *
              </label>
              <input
                type="text"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                placeholder="Ex: Loyer mensuel, Salaire, Facture électricité..."
                required
              />
            </div>

            <button
              type="submit"
              className="w-full px-6 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700"
            >
              Créer le {type === 'virement' ? 'virement' : 'prélèvement'} récurrent
            </button>
          </form>
        </div>
      )}

      <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
        <p className="text-sm text-yellow-900">
          ℹ️ Les paiements récurrents seront exécutés automatiquement selon la fréquence définie.
        </p>
      </div>
    </div>
  );
}

// SATD Tab
function SATDTab({ token, satdList, loadData }) {
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    debtor_name: '',
    debtor_entity: 'Trésor Public Français',
    amount_claimed: '',
    description: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.post(`${API_URL}/satd`, {
        ...formData,
        amount_claimed: parseFloat(formData.amount_claimed)
      }, config);
      alert('SATD créée avec succès !');
      setShowForm(false);
      setFormData({ debtor_name: '', debtor_entity: 'Trésor Public Français', amount_claimed: '', description: '' });
      loadData();
    } catch (err) {
      alert('Erreur: ' + (err.response?.data?.detail || err.message));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold text-gray-900">Gestion des SATD</h2>
        <button
          onClick={() => setShowForm(!showForm)}
          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
        >
          {showForm ? 'Annuler' : '+ Nouvelle SATD'}
        </button>
      </div>

      {showForm && (
        <div className="bg-white rounded-lg border-2 border-red-200 p-6">
          <h3 className="text-lg font-bold mb-4">Créer une SATD</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nom du débiteur *
              </label>
              <input
                type="text"
                value={formData.debtor_name}
                onChange={(e) => setFormData({ ...formData, debtor_name: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                placeholder="Ex: État Français"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Entité débitrice *
              </label>
              <select
                value={formData.debtor_entity}
                onChange={(e) => setFormData({ ...formData, debtor_entity: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              >
                <option value="Trésor Public Français">Trésor Public Français</option>
                <option value="Ministère des Finances">Ministère des Finances</option>
                <option value="Direction Générale des Finances Publiques">DGFIP</option>
                <option value="Autre">Autre entité</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Montant réclamé (€) *
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.amount_claimed}
                onChange={(e) => setFormData({ ...formData, amount_claimed: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description / Motif *
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                rows="3"
                placeholder="Motif de la saisie administrative"
                required
              />
            </div>

            <button
              type="submit"
              className="w-full px-6 py-3 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700"
            >
              Créer la SATD
            </button>
          </form>
        </div>
      )}

      {/* SATD List */}
      <div className="space-y-4">
        <h3 className="text-lg font-bold text-gray-900">Liste des SATD actives</h3>
        {satdList.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm p-12 border border-gray-200 text-center">
            <p className="text-gray-500">Aucune SATD pour le moment</p>
          </div>
        ) : (
          satdList.map((satd) => (
            <div
              key={satd.id}
              className="bg-white rounded-xl shadow-sm p-6 border border-gray-200"
            >
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-2xl">⚖️</span>
                    <h4 className="text-lg font-bold text-gray-900">
                      {satd.reference_number}
                    </h4>
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      satd.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                      satd.status === 'completed' ? 'bg-green-100 text-green-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      {satd.status}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">
                    {satd.debtor_name} - {satd.debtor_entity}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-600">Montant réclamé</p>
                  <p className="text-2xl font-bold text-red-600">
                    {satd.amount_claimed?.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
                  </p>
                </div>
              </div>

              <p className="text-gray-700 mb-4">{satd.description}</p>

              <div className="grid grid-cols-3 gap-4 pt-4 border-t border-gray-200">
                <div>
                  <p className="text-xs text-gray-600">Date de création</p>
                  <p className="font-semibold">
                    {new Date(satd.created_at).toLocaleDateString('fr-FR')}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-600">Montant récupéré</p>
                  <p className="font-semibold text-green-600">
                    {satd.recovery_amount?.toFixed(2)} €
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-600">Devise</p>
                  <p className="font-semibold">{satd.currency}</p>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// ==================== CENTRAL BANK TAB ====================
function CentralBankTab({ token }) {
  const [subTab, setSubTab] = useState('dashboard');
  const [dashboard, setDashboard] = useState(null);
  const [banks, setBanks] = useState([]);
  const [rates, setRates] = useState(null);
  const [operations, setOperations] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadCentralBankData();
  }, [subTab]);

  const loadCentralBankData = async () => {
    setLoading(true);
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      if (subTab === 'dashboard') {
        const res = await axios.get(`${API_URL}/central-bank/dashboard`, config);
        setDashboard(res.data);
      } else if (subTab === 'banks') {
        const res = await axios.get(`${API_URL}/central-bank/banks`, config);
        setBanks(res.data);
      } else if (subTab === 'rates') {
        const res = await axios.get(`${API_URL}/central-bank/rates`);
        setRates(res.data);
      } else if (subTab === 'operations') {
        const res = await axios.get(`${API_URL}/central-bank/operations`, config);
        setOperations(res.data);
      }
    } catch (err) {
      console.error('Error:', err);
      alert('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-xl p-6 text-white">
        <h2 className="text-2xl font-bold mb-2">🏛️ Banque Centrale de Bretagne</h2>
        <p className="text-purple-100">Gestion interbancaire, supervision et politique monétaire</p>
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-2 border-b border-gray-200">
        <button
          onClick={() => setSubTab('dashboard')}
          className={`px-4 py-2 font-medium border-b-2 ${
            subTab === 'dashboard' ? 'border-purple-600 text-purple-600' : 'border-transparent text-gray-500'
          }`}
        >
          📊 Dashboard
        </button>
        <button
          onClick={() => setSubTab('banks')}
          className={`px-4 py-2 font-medium border-b-2 ${
            subTab === 'banks' ? 'border-purple-600 text-purple-600' : 'border-transparent text-gray-500'
          }`}
        >
          🏦 Banques
        </button>
        <button
          onClick={() => setSubTab('rates')}
          className={`px-4 py-2 font-medium border-b-2 ${
            subTab === 'rates' ? 'border-purple-600 text-purple-600' : 'border-transparent text-gray-500'
          }`}
        >
          📈 Taux Directeurs
        </button>
        <button
          onClick={() => setSubTab('operations')}
          className={`px-4 py-2 font-medium border-b-2 ${
            subTab === 'operations' ? 'border-purple-600 text-purple-600' : 'border-transparent text-gray-500'
          }`}
        >
          💼 Opérations
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12">Chargement...</div>
      ) : (
        <>
          {subTab === 'dashboard' && dashboard && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-blue-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600">Banques enregistrées</p>
                  <p className="text-3xl font-bold text-blue-600">{dashboard.total_banks}</p>
                </div>
                <div className="bg-green-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600">Banques actives</p>
                  <p className="text-3xl font-bold text-green-600">{dashboard.active_banks}</p>
                </div>
                <div className="bg-purple-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600">Réserves totales</p>
                  <p className="text-2xl font-bold text-purple-600">
                    {dashboard.total_reserves?.toLocaleString('fr-FR')} €
                  </p>
                </div>
                <div className="bg-orange-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600">Dépôts totaux</p>
                  <p className="text-2xl font-bold text-orange-600">
                    {dashboard.total_deposits?.toLocaleString('fr-FR')} €
                  </p>
                </div>
              </div>

              {dashboard.current_rates && (
                <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl p-6 text-white">
                  <h3 className="text-xl font-bold mb-4">Taux Directeurs Actuels</h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-white/20 rounded-lg p-4">
                      <p className="text-sm text-indigo-100">Taux de Base</p>
                      <p className="text-3xl font-bold">{dashboard.current_rates.base_rate}%</p>
                    </div>
                    <div className="bg-white/20 rounded-lg p-4">
                      <p className="text-sm text-indigo-100">Taux de Dépôt</p>
                      <p className="text-3xl font-bold">{dashboard.current_rates.deposit_rate}%</p>
                    </div>
                    <div className="bg-white/20 rounded-lg p-4">
                      <p className="text-sm text-indigo-100">Taux Marginal</p>
                      <p className="text-3xl font-bold">{dashboard.current_rates.marginal_rate}%</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {subTab === 'banks' && (
            <div className="space-y-4">
              <div className="flex justify-end">
                <button className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700">
                  + Enregistrer une banque
                </button>
              </div>
              {banks.map(bank => (
                <div key={bank.id} className="bg-white border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="text-lg font-bold">{bank.bank_name}</h3>
                      <p className="text-sm text-gray-600">Code: {bank.bank_code} | BIC: {bank.bic}</p>
                      <p className="text-sm text-gray-600 mt-1">📍 {bank.address}</p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      bank.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                    }`}>
                      {bank.status}
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t">
                    <div>
                      <p className="text-xs text-gray-600">Réserves obligatoires</p>
                      <p className="font-semibold">{(bank.required_reserves_ratio * 100).toFixed(1)}%</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-600">Réserves actuelles</p>
                      <p className="font-semibold">{bank.current_reserves?.toLocaleString('fr-FR')} €</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-600">Dépôts totaux</p>
                      <p className="font-semibold">{bank.total_deposits?.toLocaleString('fr-FR')} €</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {subTab === 'rates' && rates && (
            <div className="space-y-4">
              <div className="bg-indigo-50 rounded-lg p-6">
                <h3 className="text-lg font-bold mb-4">Taux Directeurs en vigueur</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-white rounded-lg p-4 border-2 border-indigo-200">
                    <p className="text-sm text-gray-600 mb-1">Taux de Base (Refinancement)</p>
                    <p className="text-4xl font-bold text-indigo-600">{rates.base_rate}%</p>
                    <p className="text-xs text-gray-500 mt-2">Prêts aux banques commerciales</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 border-2 border-green-200">
                    <p className="text-sm text-gray-600 mb-1">Taux de Dépôt</p>
                    <p className="text-4xl font-bold text-green-600">{rates.deposit_rate}%</p>
                    <p className="text-xs text-gray-500 mt-2">Rémunération des dépôts</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 border-2 border-red-200">
                    <p className="text-sm text-gray-600 mb-1">Taux Marginal</p>
                    <p className="text-4xl font-bold text-red-600">{rates.marginal_rate}%</p>
                    <p className="text-xs text-gray-500 mt-2">Prêts d'urgence</p>
                  </div>
                </div>
                {rates.notes && (
                  <div className="mt-4 p-3 bg-white rounded-lg">
                    <p className="text-sm text-gray-700"><strong>Notes:</strong> {rates.notes}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {subTab === 'operations' && (
            <div className="space-y-4">
              <div className="flex justify-end">
                <button className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700">
                  + Nouvelle opération
                </button>
              </div>
              {operations.map(op => (
                <div key={op.id} className="bg-white border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h4 className="font-bold">{op.reference_number}</h4>
                      <p className="text-sm text-gray-600">{op.operation_type}</p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      op.status === 'completed' ? 'bg-green-100 text-green-700' :
                      op.status === 'approved' ? 'bg-blue-100 text-blue-700' :
                      'bg-yellow-100 text-yellow-700'
                    }`}>
                      {op.status}
                    </span>
                  </div>
                  <div className="grid grid-cols-4 gap-4">
                    <div>
                      <p className="text-xs text-gray-600">Montant</p>
                      <p className="font-semibold">{op.amount?.toLocaleString('fr-FR')} €</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-600">Taux</p>
                      <p className="font-semibold">{op.interest_rate}%</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-600">Durée</p>
                      <p className="font-semibold">{op.duration_days || 'N/A'} jours</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-600">Date</p>
                      <p className="font-semibold text-sm">
                        {new Date(op.created_at).toLocaleDateString('fr-FR')}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ==================== KYC TAB ====================
function KYCTab({ token }) {
  const [pendingKYC, setPendingKYC] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadPendingKYC();
  }, []);

  const loadPendingKYC = async () => {
    setLoading(true);
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const res = await axios.get(`${API_URL}/kyc/pending`, config);
      setPendingKYC(res.data);
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyKYC = async (kycId, status, reason = '') => {
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.put(`${API_URL}/kyc/${kycId}/verify`, {
        status,
        rejection_reason: reason
      }, config);
      alert(`KYC ${status === 'verified' ? 'vérifié' : 'rejeté'} avec succès !`);
      loadPendingKYC();
    } catch (err) {
      alert('Erreur: ' + (err.response?.data?.detail || err.message));
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-green-600 to-teal-600 rounded-xl p-6 text-white">
        <h2 className="text-2xl font-bold mb-2">🆔 Vérification KYC</h2>
        <p className="text-green-100">Validation des documents d'identité avec extraction OCR automatique</p>
      </div>

      {loading ? (
        <div className="text-center py-12">Chargement...</div>
      ) : pendingKYC.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <p className="text-gray-600">✓ Aucun KYC en attente de vérification</p>
        </div>
      ) : (
        <div className="space-y-4">
          {pendingKYC.map(kyc => (
            <div key={kyc.id} className="bg-white border-2 border-gray-200 rounded-xl p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-bold">Document KYC</h3>
                  <p className="text-sm text-gray-600">Type: {kyc.document_type}</p>
                  <p className="text-xs text-gray-500">Uploadé le: {new Date(kyc.uploaded_at).toLocaleString('fr-FR')}</p>
                </div>
                <span className="px-3 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-700">
                  En attente
                </span>
              </div>

              {/* Données extraites par OCR */}
              {kyc.extracted_data && Object.keys(kyc.extracted_data).length > 0 && (
                <div className="bg-blue-50 rounded-lg p-4 mb-4">
                  <h4 className="font-semibold mb-2">📄 Données extraites (OCR)</h4>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    {Object.entries(kyc.extracted_data).map(([key, value]) => (
                      <div key={key}>
                        <span className="text-gray-600">{key}:</span>
                        <span className="ml-2 font-medium">{value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Images */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <p className="text-sm font-medium mb-2">Recto</p>
                  <div className="bg-gray-100 rounded-lg p-2 text-center text-gray-500 text-sm">
                    Image disponible sur serveur
                  </div>
                </div>
                {kyc.back_image_url && (
                  <div>
                    <p className="text-sm font-medium mb-2">Verso</p>
                    <div className="bg-gray-100 rounded-lg p-2 text-center text-gray-500 text-sm">
                      Image disponible sur serveur
                    </div>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <button
                  onClick={() => handleVerifyKYC(kyc.id, 'verified')}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold"
                >
                  ✓ Vérifier
                </button>
                <button
                  onClick={() => {
                    const reason = prompt('Raison du rejet:');
                    if (reason) handleVerifyKYC(kyc.id, 'rejected', reason);
                  }}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-semibold"
                >
                  ✗ Rejeter
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ==================== LOANS MANAGEMENT TAB ====================
function LoansManagementTab({ token }) {
  const [loans, setLoans] = useState([]);
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [loanScore, setLoanScore] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadAllLoans();
  }, []);

  const loadAllLoans = async () => {
    setLoading(true);
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const res = await axios.get(`${API_URL}/admin/loans`, config);
      setLoans(res.data);
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const calculateScore = async (loanId) => {
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const res = await axios.post(`${API_URL}/loans/${loanId}/calculate-score`, {}, config);
      setLoanScore(res.data);
      alert('Score calculé avec succès !');
    } catch (err) {
      alert('Erreur: ' + (err.response?.data?.detail || err.message));
    }
  };

  const makeDecision = async (loanId, decision, amountAdjusted, rateAdjusted, notes) => {
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.put(`${API_URL}/loans/${loanId}/admin-decision`, {
        decision,
        amount_approved: amountAdjusted || null,
        interest_rate_adjusted: rateAdjusted || null,
        admin_notes: notes
      }, config);
      alert(`Crédit ${decision === 'approved' ? 'approuvé' : 'rejeté'} !`);
      loadAllLoans();
      setSelectedLoan(null);
      setLoanScore(null);
    } catch (err) {
      alert('Erreur: ' + (err.response?.data?.detail || err.message));
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-yellow-600 to-orange-600 rounded-xl p-6 text-white">
        <h2 className="text-2xl font-bold mb-2">💰 Gestion des Crédits</h2>
        <p className="text-yellow-100">Calcul de solvabilité et décisions administratives</p>
      </div>

      {loading ? (
        <div className="text-center py-12">Chargement...</div>
      ) : (
        <div className="space-y-4">
          {loans.filter(l => l.status === 'pending').map(loan => (
            <div key={loan.id} className="bg-white border-2 border-gray-200 rounded-xl p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-bold">{loan.reference_number}</h3>
                  <p className="text-sm text-gray-600">Type: {loan.loan_type}</p>
                  <p className="text-xs text-gray-500">Demandé le: {new Date(loan.created_at).toLocaleDateString('fr-FR')}</p>
                </div>
                <span className="px-3 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-700">
                  En attente
                </span>
              </div>

              <div className="grid grid-cols-4 gap-4 mb-4">
                <div>
                  <p className="text-xs text-gray-600">Montant demandé</p>
                  <p className="font-bold text-lg">{loan.amount_requested?.toLocaleString('fr-FR')} €</p>
                </div>
                <div>
                  <p className="text-xs text-gray-600">Durée</p>
                  <p className="font-semibold">{loan.duration_months} mois</p>
                </div>
                <div>
                  <p className="text-xs text-gray-600">Taux</p>
                  <p className="font-semibold">{loan.interest_rate}%</p>
                </div>
                <div>
                  <p className="text-xs text-gray-600">Mensualité estimée</p>
                  <p className="font-semibold">{loan.monthly_payment?.toFixed(2) || 'N/A'} €</p>
                </div>
              </div>

              <p className="text-sm text-gray-700 mb-4"><strong>Motif:</strong> {loan.description}</p>

              {selectedLoan === loan.id && loanScore && (
                <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-lg p-4 mb-4 border-2 border-purple-200">
                  <h4 className="font-bold mb-3 flex items-center gap-2">
                    <span>📊</span> Score de Solvabilité
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                    <div className="bg-white rounded-lg p-3 text-center">
                      <p className="text-xs text-gray-600">Score Crédit</p>
                      <p className={`text-2xl font-bold ${
                        loanScore.credit_score >= 70 ? 'text-green-600' :
                        loanScore.credit_score >= 40 ? 'text-orange-600' : 'text-red-600'
                      }`}>
                        {loanScore.credit_score.toFixed(0)}/100
                      </p>
                    </div>
                    <div className="bg-white rounded-lg p-3 text-center">
                      <p className="text-xs text-gray-600">Ratio Dette/Revenus</p>
                      <p className="text-2xl font-bold text-blue-600">
                        {(loanScore.debt_to_income_ratio * 100).toFixed(0)}%
                      </p>
                    </div>
                    <div className="bg-white rounded-lg p-3 text-center">
                      <p className="text-xs text-gray-600">Balance Comptes</p>
                      <p className="text-lg font-bold text-purple-600">
                        {loanScore.account_balance?.toLocaleString('fr-FR')} €
                      </p>
                    </div>
                    <div className="bg-white rounded-lg p-3 text-center">
                      <p className="text-xs text-gray-600">Recommandation</p>
                      <p className={`text-sm font-bold uppercase ${
                        loanScore.recommendation === 'approve' ? 'text-green-600' :
                        loanScore.recommendation === 'reject' ? 'text-red-600' : 'text-orange-600'
                      }`}>
                        {loanScore.recommendation}
                      </p>
                    </div>
                  </div>
                  <p className="text-xs text-gray-600">{loanScore.notes}</p>
                </div>
              )}

              <div className="flex gap-3">
                {selectedLoan !== loan.id ? (
                  <button
                    onClick={() => {
                      setSelectedLoan(loan.id);
                      calculateScore(loan.id);
                    }}
                    className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-semibold"
                  >
                    📊 Calculer Score
                  </button>
                ) : (
                  <>
                    <button
                      onClick={() => {
                        const notes = prompt('Notes admin (optionnel):') || '';
                        makeDecision(loan.id, 'approved', null, null, notes);
                      }}
                      className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold"
                    >
                      ✓ Approuver
                    </button>
                    <button
                      onClick={() => {
                        const notes = prompt('Raison du rejet:') || '';
                        makeDecision(loan.id, 'rejected', null, null, notes);
                      }}
                      className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-semibold"
                    >
                      ✗ Rejeter
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}

          {loans.filter(l => l.status === 'pending').length === 0 && (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <p className="text-gray-600">Aucune demande de crédit en attente</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ==================== BAD CLIENTS TAB ====================
function BadClientsTab({ token }) {
  const [badClients, setBadClients] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadBadClients();
  }, []);

  const loadBadClients = async () => {
    setLoading(true);
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      // Récupérer tous les utilisateurs et leurs crédits
      const [usersRes, loansRes, accountsRes] = await Promise.all([
        axios.get(`${API_URL}/admin/users`, config),
        axios.get(`${API_URL}/admin/loans`, config),
        axios.get(`${API_URL}/admin/accounts`, config)
      ]);

      // Identifier les mauvais clients (ceux avec des crédits rejetés ou des comptes suspendus)
      const badClientsList = [];
      
      usersRes.data.forEach(user => {
        const userLoans = loansRes.data.filter(l => l.user_id === user.id);
        const userAccounts = accountsRes.data.filter(a => a.user_id === user.id);
        
        const rejectedLoans = userLoans.filter(l => l.status === 'rejected').length;
        const suspendedAccounts = userAccounts.filter(a => a.status === 'suspended').length;
        const totalDebt = userLoans
          .filter(l => ['approved', 'disbursed'].includes(l.status))
          .reduce((sum, l) => sum + (l.amount_approved || 0), 0);

        // Considérer comme "mauvais client" si :
        // - Au moins 2 crédits rejetés OU
        // - Au moins 1 compte suspendu OU
        // - Dette totale > 50 000€
        if (rejectedLoans >= 2 || suspendedAccounts >= 1 || totalDebt > 50000) {
          badClientsList.push({
            ...user,
            rejectedLoans,
            suspendedAccounts,
            totalDebt,
            activeLoans: userLoans.filter(l => ['approved', 'disbursed'].includes(l.status)).length,
            accounts: userAccounts
          });
        }
      });

      setBadClients(badClientsList);
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const markAsReviewed = async (userId, reviewed = true) => {
    const action = reviewed ? 'révisé' : 'non-révisé';
    if (confirm(`Marquer ce client comme "${action}" ?`)) {
      try {
        const config = { headers: { Authorization: `Bearer ${token}` } };
        await axios.put(`${API_URL}/admin/users/${userId}/review-status?reviewed=${reviewed}`, {}, config);
        alert(`Client marqué comme ${action} avec succès !`);
        loadBadClients();
      } catch (err) {
        alert('Erreur: ' + (err.response?.data?.detail || err.message));
      }
    }
  };

  const resolveIssues = async (client) => {
    // Menu d'options pour résoudre les problèmes
    const actions = [];
    
    if (client.suspendedAccounts > 0) {
      actions.push('1. Réactiver les comptes suspendus');
    }
    if (client.rejectedLoans >= 2) {
      actions.push('2. Revoir les crédits rejetés');
    }
    if (client.totalDebt > 50000) {
      actions.push('3. Plan de remboursement');
    }
    
    const choice = prompt(`Actions disponibles:\n${actions.join('\n')}\n\nEntrez le numéro de l'action (ou annuler):`);
    
    if (choice === '1' && client.suspendedAccounts > 0) {
      // Réactiver les comptes
      try {
        const config = { headers: { Authorization: `Bearer ${token}` } };
        for (const account of client.accounts.filter(a => a.status === 'suspended')) {
          await axios.put(`${API_URL}/admin/accounts/${account.id}`, { status: 'active' }, config);
        }
        alert('Comptes réactivés avec succès !');
        loadBadClients();
      } catch (err) {
        alert('Erreur: ' + (err.response?.data?.detail || err.message));
      }
    } else if (choice) {
      alert('Cette fonctionnalité sera bientôt disponible.');
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-red-600 to-orange-600 rounded-xl p-6 text-white">
        <h2 className="text-2xl font-bold mb-2">⚠️ Mauvais Clients</h2>
        <p className="text-red-100">Clients avec crédits rejetés, comptes suspendus ou endettement élevé</p>
      </div>

      {loading ? (
        <div className="text-center py-12">Chargement...</div>
      ) : badClients.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <p className="text-gray-600">✓ Aucun client problématique détecté</p>
        </div>
      ) : (
        <div className="space-y-4">
          {badClients.map(client => (
            <div key={client.id} className="bg-white border-2 border-red-200 rounded-xl p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-bold text-gray-900">{client.name}</h3>
                  <p className="text-sm text-gray-600">{client.email}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    Inscrit le: {new Date(client.created_at).toLocaleDateString('fr-FR')}
                  </p>
                </div>
                <div className="flex gap-2">
                  {client.suspendedAccounts > 0 && (
                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700">
                      🔒 {client.suspendedAccounts} compte(s) suspendu(s)
                    </span>
                  )}
                  {!client.is_active && (
                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-700">
                      Inactif
                    </span>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-4 gap-4 mb-4 p-4 bg-red-50 rounded-lg">
                <div>
                  <p className="text-xs text-gray-600">Crédits rejetés</p>
                  <p className="text-2xl font-bold text-red-600">{client.rejectedLoans}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-600">Crédits actifs</p>
                  <p className="text-2xl font-bold text-orange-600">{client.activeLoans}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-600">Dette totale</p>
                  <p className="text-xl font-bold text-red-600">
                    {client.totalDebt.toLocaleString('fr-FR')} €
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-600">Comptes</p>
                  <p className="text-2xl font-bold text-blue-600">{client.accounts.length}</p>
                </div>
              </div>

              <div className="flex gap-3 flex-wrap">
                <button
                  onClick={() => markAsReviewed(client.id, true)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold"
                >
                  ✓ Marquer comme révisé
                </button>
                {client.suspendedAccounts > 0 && (
                  <button
                    onClick={() => resolveIssues(client)}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold"
                  >
                    🔧 Résoudre problèmes
                  </button>
                )}
                <button
                  onClick={() => markAsReviewed(client.id, false)}
                  className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 font-semibold"
                >
                  ↻ Retirer alerte
                </button>
              </div>

              {/* Raisons d'alerte */}
              <div className="mt-4 pt-4 border-t border-red-200">
                <p className="text-sm font-semibold text-red-900 mb-2">🚨 Alertes :</p>
                <ul className="text-sm text-gray-700 space-y-1">
                  {client.rejectedLoans >= 2 && (
                    <li>• <strong className="text-red-600">{client.rejectedLoans} crédits rejetés</strong> - Historique de crédit problématique</li>
                  )}
                  {client.suspendedAccounts >= 1 && (
                    <li>• <strong className="text-red-600">{client.suspendedAccounts} compte(s) suspendu(s)</strong> - Activité suspecte détectée</li>
                  )}
                  {client.totalDebt > 50000 && (
                    <li>• <strong className="text-red-600">Dette élevée ({client.totalDebt.toLocaleString('fr-FR')} €)</strong> - Risque de défaut de paiement</li>
                  )}
                </ul>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ==================== CLOSED ACCOUNTS TAB ====================
function ClosedAccountsTab({ token }) {
  const [closedAccounts, setClosedAccounts] = useState([]);
  const [users, setUsers] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadClosedAccounts();
  }, []);

  const loadClosedAccounts = async () => {
    setLoading(true);
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const [accountsRes, usersRes] = await Promise.all([
        axios.get(`${API_URL}/admin/accounts`, config),
        axios.get(`${API_URL}/admin/users`, config)
      ]);

      // Filtrer les comptes clôturés (status = 'closed' ou 'suspended')
      const closed = accountsRes.data.filter(acc => acc.status === 'closed' || acc.status === 'suspended');
      setClosedAccounts(closed);

      const usersMap = {};
      usersRes.data.forEach(user => {
        usersMap[user.id] = user;
      });
      setUsers(usersMap);
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const reopenAccount = async (accountId) => {
    if (confirm('Êtes-vous sûr de vouloir réouvrir ce compte ?')) {
      try {
        const config = { headers: { Authorization: `Bearer ${token}` } };
        await axios.put(`${API_URL}/admin/accounts/${accountId}`, {
          status: 'active'
        }, config);
        alert('Compte réouvert avec succès !');
        loadClosedAccounts();
      } catch (err) {
        alert('Erreur: ' + (err.response?.data?.detail || err.message));
      }
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-gray-600 to-slate-700 rounded-xl p-6 text-white">
        <h2 className="text-2xl font-bold mb-2">🔒 Comptes Clôturés</h2>
        <p className="text-gray-200">Comptes fermés ou suspendus</p>
      </div>

      {loading ? (
        <div className="text-center py-12">Chargement...</div>
      ) : closedAccounts.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <p className="text-gray-600">Aucun compte clôturé</p>
        </div>
      ) : (
        <div className="space-y-4">
          {closedAccounts.map(account => (
            <div key={account.id} className="bg-white border-2 border-gray-300 rounded-xl p-6 opacity-75">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-bold text-gray-900">
                    {users[account.user_id]?.name || 'Utilisateur inconnu'}
                  </h3>
                  <p className="text-sm text-gray-600">{users[account.user_id]?.email}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    IBAN: <span className="font-mono">{account.iban}</span>
                  </p>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                  account.status === 'closed' ? 'bg-gray-100 text-gray-700' : 'bg-red-100 text-red-700'
                }`}>
                  {account.status === 'closed' ? '🔒 Clôturé' : '⚠️ Suspendu'}
                </span>
              </div>

              <div className="grid grid-cols-3 gap-4 mb-4 p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-xs text-gray-600">Type de compte</p>
                  <p className="font-semibold text-gray-900">{account.account_type}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-600">Solde final</p>
                  <p className="text-lg font-bold text-gray-900">
                    {account.balance?.toFixed(2)} €
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-600">Date de création</p>
                  <p className="font-semibold text-sm">
                    {new Date(account.created_at).toLocaleDateString('fr-FR')}
                  </p>
                </div>
              </div>

              {account.status === 'suspended' && (
                <div className="mb-4 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                  <p className="text-sm text-yellow-900">
                    ⚠️ <strong>Compte suspendu</strong> - Ce compte peut être réouvert si la situation est régularisée.
                  </p>
                </div>
              )}

              <div className="flex gap-3">
                {account.status === 'suspended' && (
                  <button
                    onClick={() => reopenAccount(account.id)}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold"
                  >
                    🔓 Réouvrir le compte
                  </button>
                )}
                <button
                  className="px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-semibold"
                >
                  📄 Voir historique complet
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Staff Management Tab
function StaffManagementTab({ token }) {
  const [staffMembers, setStaffMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role_type: 'moderator'
  });

  useEffect(() => {
    loadStaffMembers();
  }, []);

  const loadStaffMembers = async () => {
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`${API_URL}/admin/users`, config);
      
      // Filtrer uniquement les membres du staff
      const staff = response.data.filter(user => 
        ['founder', 'admin', 'deputy_admin', 'moderator', 'account_manager'].includes(user.role?.role_type)
      );
      
      setStaffMembers(staff);
      setLoading(false);
    } catch (error) {
      console.error('Error loading staff members:', error);
      setLoading(false);
    }
  };

  const handleCreateStaff = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.password) {
      alert('Veuillez remplir tous les champs');
      return;
    }

    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.post(`${API_URL}/admin/create-staff`, formData, config);
      
      alert(`Staff ${formData.name} créé avec succès !`);
      setShowCreateForm(false);
      setFormData({ name: '', email: '', password: '', role_type: 'moderator' });
      loadStaffMembers();
    } catch (error) {
      alert(error.response?.data?.detail || 'Erreur lors de la création du staff');
    }
  };

  const getRoleBadge = (roleType) => {
    const badges = {
      admin: { text: 'Administrateur', color: 'bg-red-100 text-red-800', icon: '👑' },
      deputy_admin: { text: 'Admin Adjoint', color: 'bg-orange-100 text-orange-800', icon: '👔' },
      moderator: { text: 'Modérateur', color: 'bg-blue-100 text-blue-800', icon: '🛡️' },
      account_manager: { text: 'Gestionnaire Comptes', color: 'bg-green-100 text-green-800', icon: '📊' }
    };
    
    const badge = badges[roleType] || { text: roleType, color: 'bg-gray-100 text-gray-800', icon: '👤' };
    
    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ${badge.color}`}>
        <span>{badge.icon}</span>
        <span>{badge.text}</span>
      </span>
    );
  };

  const getPermissionsDescription = (roleType) => {
    const descriptions = {
      admin: 'Accès complet à toutes les fonctionnalités',
      deputy_admin: 'Accès complet sauf banque centrale et gestion admins',
      moderator: 'KYC vérification, consultation crédits/comptes (lecture seule)',
      account_manager: 'Gestion complète des comptes, KYC et crédits (pas banque centrale)'
    };
    return descriptions[roleType] || 'Aucune permission';
  };

  if (loading) {
    return <div className="text-center py-8">Chargement...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Gestion du Staff Interne</h2>
          <p className="text-sm text-gray-600 mt-1">
            Gérer les administrateurs, modérateurs et gestionnaires de comptes
          </p>
        </div>
        <button
          onClick={() => setShowCreateForm(!showCreateForm)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold flex items-center gap-2"
        >
          {showCreateForm ? '❌ Annuler' : '➕ Créer Staff'}
        </button>
      </div>

      {/* Create Form */}
      {showCreateForm && (
        <div className="bg-white rounded-lg shadow-md border-2 border-blue-200 p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Créer un nouveau membre du staff</h3>
          
          <form onSubmit={handleCreateStaff} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nom complet *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Jean Dupont"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email *
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="jean.dupont@bkb.bz"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mot de passe *
                </label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="••••••••"
                  required
                  minLength="6"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Rôle *
                </label>
                <select
                  value={formData.role_type}
                  onChange={(e) => setFormData({ ...formData, role_type: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="moderator">🛡️ Modérateur</option>
                  <option value="account_manager">📊 Gestionnaire de Comptes</option>
                  <option value="deputy_admin">👔 Administrateur Adjoint</option>
                  <option value="admin">👑 Administrateur</option>
                </select>
              </div>
            </div>

            <div className="bg-blue-50 rounded-lg p-4">
              <p className="text-sm text-blue-900 font-semibold mb-1">
                ℹ️ Permissions pour : {getRoleBadge(formData.role_type)}
              </p>
              <p className="text-xs text-blue-700">
                {getPermissionsDescription(formData.role_type)}
              </p>
            </div>

            <div className="flex gap-3">
              <button
                type="submit"
                className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700"
              >
                ✅ Créer le staff
              </button>
              <button
                type="button"
                onClick={() => setShowCreateForm(false)}
                className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50"
              >
                Annuler
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Staff List */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">
          Liste des membres du staff ({staffMembers.length})
        </h3>

        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Nom & Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Rôle
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Permissions
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date création
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {staffMembers.map((staff) => (
                <tr key={staff.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div>
                      <div className="text-sm font-semibold text-gray-900">{staff.name}</div>
                      <div className="text-xs text-gray-500">{staff.email}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {getRoleBadge(staff.role?.role_type)}
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-xs text-gray-600">
                      {getPermissionsDescription(staff.role?.role_type)}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-xs text-gray-500">
                      {staff.created_at ? new Date(staff.created_at).toLocaleDateString('fr-FR') : 'N/A'}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {staffMembers.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              Aucun membre du staff trouvé
            </div>
          )}
        </div>
      </div>

      {/* Permissions Info */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 border-2 border-blue-200">
        <h3 className="text-lg font-bold text-gray-900 mb-4">📋 Détail des permissions par rôle</h3>
        
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <div className="flex items-center gap-2 mb-2">
              {getRoleBadge('admin')}
            </div>
            <ul className="text-xs text-gray-700 space-y-1">
              <li>✅ Accès complet à toutes les fonctionnalités</li>
              <li>✅ Gestion du système interbancaire</li>
              <li>✅ Création/suppression d'administrateurs</li>
            </ul>
          </div>

          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <div className="flex items-center gap-2 mb-2">
              {getRoleBadge('deputy_admin')}
            </div>
            <ul className="text-xs text-gray-700 space-y-1">
              <li>✅ Toutes permissions sauf banque centrale</li>
              <li>✅ Gestion KYC, crédits, comptes, utilisateurs</li>
              <li>❌ Pas de gestion d'admins</li>
            </ul>
          </div>

          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <div className="flex items-center gap-2 mb-2">
              {getRoleBadge('moderator')}
            </div>
            <ul className="text-xs text-gray-700 space-y-1">
              <li>✅ Vérification et approbation KYC</li>
              <li>✅ Consultation crédits et comptes (lecture seule)</li>
              <li>❌ Pas de modifications de comptes</li>
              <li>❌ Pas de gestion utilisateurs</li>
            </ul>
          </div>

          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <div className="flex items-center gap-2 mb-2">
              {getRoleBadge('account_manager')}
            </div>
            <ul className="text-xs text-gray-700 space-y-1">
              <li>✅ Gestion complète des comptes</li>
              <li>✅ Vérification KYC + décisions crédits</li>
              <li>✅ Création de clients uniquement</li>
              <li>❌ Pas d'accès banque centrale</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

// ELB Management Tab (Admin only)
function ELBManagementTab({ token }) {
  const [supply, setSupply] = useState(null);
  const [wallets, setWallets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mintForm, setMintForm] = useState({
    amount_elb: '',
    recipient_wallet_address: '',
    notes: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const supplyRes = await axios.get(`${API_URL}/central-bank/elb/supply`, config);
      setSupply(supplyRes.data);
      
      const walletsRes = await axios.get(`${API_URL}/central-bank/elb/wallets`, config);
      setWallets(walletsRes.data.wallets || []);
      
      setLoading(false);
    } catch (error) {
      console.error('Error loading e-£B data:', error);
      setLoading(false);
    }
  };

  const handleMint = async (e) => {
    e.preventDefault();
    
    if (!mintForm.amount_elb || parseFloat(mintForm.amount_elb) <= 0) {
      alert('Montant invalide');
      return;
    }

    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.post(`${API_URL}/central-bank/elb/mint`, mintForm, config);
      
      alert(response.data.message);
      setMintForm({ amount_elb: '', recipient_wallet_address: '', notes: '' });
      loadData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Erreur lors de l\'émission');
    }
  };

  if (loading) {
    return <div className="text-center py-8">Chargement...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header with Stats */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-lg p-6 text-white">
          <p className="text-sm font-semibold mb-1">Supply Totale e-£B</p>
          <p className="text-3xl font-bold">{supply?.total_supply_elb?.toFixed(2) || 0} £B</p>
          <p className="text-xs mt-1 opacity-90">≈ {supply?.total_supply_eur_equivalent?.toFixed(2) || 0} EUR</p>
        </div>
        
        <div className="bg-blue-500 rounded-lg p-6 text-white">
          <p className="text-sm font-semibold mb-1">Portefeuilles Actifs</p>
          <p className="text-3xl font-bold">{supply?.wallet_count || 0}</p>
        </div>
        
        <div className="bg-green-500 rounded-lg p-6 text-white">
          <p className="text-sm font-semibold mb-1">Transactions</p>
          <p className="text-3xl font-bold">{supply?.transaction_count || 0}</p>
        </div>
        
        <div className="bg-purple-500 rounded-lg p-6 text-white">
          <p className="text-sm font-semibold mb-1">Taux</p>
          <p className="text-3xl font-bold">1,25 €</p>
          <p className="text-xs mt-1 opacity-90">par 1 £B</p>
        </div>
      </div>

      {/* Mint Form */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">🏦 Émettre des e-£B (Minting)</h3>
        
        <form onSubmit={handleMint} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Montant e-£B *
              </label>
              <input
                type="number"
                step="0.01"
                min="0.01"
                value={mintForm.amount_elb}
                onChange={(e) => setMintForm({ ...mintForm, amount_elb: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="1000.00"
                required
              />
              <p className="text-xs text-gray-600 mt-1">
                ≈ {mintForm.amount_elb ? (parseFloat(mintForm.amount_elb) * 1.25).toFixed(2) : '0'} EUR
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Destinataire (optionnel)
              </label>
              <input
                type="text"
                value={mintForm.recipient_wallet_address}
                onChange={(e) => setMintForm({ ...mintForm, recipient_wallet_address: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                placeholder="0xBZ... (vide = réserves)"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes
            </label>
            <textarea
              value={mintForm.notes}
              onChange={(e) => setMintForm({ ...mintForm, notes: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              rows="2"
              placeholder="Raison de l'émission..."
            />
          </div>

          <button
            type="submit"
            className="w-full px-6 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700"
          >
            ✅ Émettre e-£B
          </button>
        </form>
      </div>

      {/* Wallets List */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">
          💰 Portefeuilles e-£B ({wallets.length})
        </h3>

        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Utilisateur</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Adresse</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Solde e-£B</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Équivalent EUR</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {wallets.slice(0, 20).map((wallet) => (
                <tr key={wallet.id}>
                  <td className="px-6 py-4">
                    <div className="text-sm font-semibold text-gray-900">{wallet.user_name || 'N/A'}</div>
                    <div className="text-xs text-gray-500">{wallet.user_email || 'N/A'}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-xs font-mono text-gray-600">
                      {wallet.wallet_address?.substring(0, 20)}...
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="text-sm font-bold text-yellow-600">
                      {wallet.balance_elb?.toFixed(2) || 0} £B
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="text-sm text-gray-700">
                      {wallet.balance_eur_equivalent?.toFixed(2) || 0} €
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {wallets.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              Aucun portefeuille créé
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Currency Monitor Tab (Admin/Staff)
function CurrencyMonitorTab({ token }) {
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    loadRates();
  }, []);

  const loadRates = async () => {
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`${API_URL}/central-bank/currencies/rates`, config);
      setRates(response.data.rates || []);
      setLoading(false);
    } catch (error) {
      console.error('Error loading rates:', error);
      setLoading(false);
    }
  };

  const handleUpdateRates = async () => {
    setUpdating(true);
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.post(`${API_URL}/central-bank/currencies/update`, {}, config);
      
      alert(`Taux mis à jour avec succès ! ${response.data.rates?.length || 0} devises`);
      loadRates();
    } catch (error) {
      alert(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return <div className="text-center py-8">Chargement...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">📊 Monitoring des Devises</h2>
          <p className="text-sm text-gray-600 mt-1">
            Surveillance EUR vs devises mondiales (USD, RUB, JPY, INR, CNY)
          </p>
        </div>
        <button
          onClick={handleUpdateRates}
          disabled={updating}
          className="px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:bg-gray-400"
        >
          {updating ? '⏳ Mise à jour...' : '🔄 Actualiser les taux'}
        </button>
      </div>

      {/* Alert Info */}
      <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4">
        <p className="text-sm text-red-900">
          <strong>⚠️ Alerte automatique:</strong> Le système déclenchera une alerte si l'EUR perd plus de 5% face à l'une de ces devises.
        </p>
      </div>

      {/* Rates Grid */}
      <div className="grid grid-cols-2 gap-4">
        {rates.length === 0 ? (
          <div className="col-span-2 text-center py-12 bg-white rounded-lg shadow-md">
            <p className="text-gray-500 mb-4">Aucun taux disponible</p>
            <button
              onClick={handleUpdateRates}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700"
            >
              Charger les taux
            </button>
          </div>
        ) : (
          rates.map((rate) => (
            <div
              key={rate.target_currency}
              className={`bg-white rounded-lg shadow-md p-6 border-2 ${
                rate.alert_triggered ? 'border-red-500 bg-red-50' : 'border-gray-200'
              }`}
            >
              <div className="flex justify-between items-start mb-4">
                <div>
                  <p className="text-sm text-gray-600 font-semibold">EUR → {rate.target_currency}</p>
                  <p className="text-3xl font-bold text-gray-900 mt-1">
                    {rate.rate?.toFixed(4)}
                  </p>
                </div>
                <div className={`px-3 py-1 rounded-full text-xs font-semibold ${
                  rate.change_24h > 0 ? 'bg-green-100 text-green-800' :
                  rate.change_24h < 0 ? 'bg-red-100 text-red-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {rate.change_24h > 0 ? '↑' : rate.change_24h < 0 ? '↓' : '→'} {Math.abs(rate.change_24h).toFixed(2)}%
                </div>
              </div>

              <div className="text-xs text-gray-600 space-y-1">
                <p>1 EUR = {rate.rate?.toFixed(4)} {rate.target_currency}</p>
                <p>1 {rate.target_currency} = {rate.rate_reverse?.toFixed(4)} EUR</p>
                <p className="text-xs text-gray-500 mt-2">
                  Mis à jour: {new Date(rate.timestamp).toLocaleString('fr-FR')}
                </p>
              </div>

              {rate.alert_triggered && (
                <div className="mt-4 bg-red-100 border border-red-300 rounded px-3 py-2">
                  <p className="text-xs text-red-800 font-bold">
                    🚨 ALERTE: EUR en baisse significative !
                  </p>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* e-£B Info */}
      <div className="bg-gradient-to-r from-yellow-50 to-yellow-100 rounded-lg p-6 border-2 border-yellow-300">
        <h3 className="text-lg font-bold text-gray-900 mb-3">
          💎 Livre Bretonne Électronique (e-£B)
        </h3>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <p className="text-sm text-yellow-800 font-semibold">Taux fixe</p>
            <p className="text-2xl font-bold text-yellow-900">1 £B = 1,25 €</p>
          </div>
          <div>
            <p className="text-sm text-yellow-800 font-semibold">Protection</p>
            <p className="text-sm text-yellow-900">Indépendant des fluctuations EUR</p>
          </div>
          <div>
            <p className="text-sm text-yellow-800 font-semibold">Avantage</p>
            <p className="text-sm text-yellow-900">Valeur stable même si EUR baisse</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminPanel;
